import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";

import { CONFIG } from "@/shared/model/config";

import { showBan } from "../model/ban-notification";
import { useSession } from "../model/session";

import { ApiPaths } from "./schema";

// Клиент для авторизованных запросов
export const fetchClient = createFetchClient<ApiPaths>({
  baseUrl: CONFIG.API_BASE_URL,
  credentials: "include",
  // Кастомная сериализация для Rails backend (поддержка массивов с [] скобками)
  querySerializer(queryParams) {
    const searchParams = [];

    for (const [name, value] of Object.entries(queryParams)) {
      if (value === null || value === undefined) {
        continue;
      }

      if (Array.isArray(value)) {
        // Rails формат: buyer_ids[]=9&buyer_ids[]=12
        for (const item of value) {
          if (item !== null && item !== undefined) {
            searchParams.push(
              `${encodeURIComponent(name)}[]=${encodeURIComponent(String(item))}`,
            );
          }
        }
      } else {
        // Обычные параметры
        searchParams.push(
          `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`,
        );
      }
    }

    return searchParams.join("&");
  },
});

// Клиент для публичных запросов (логин, регистрация)
export const publicFetchClient = createFetchClient<ApiPaths>({
  baseUrl: CONFIG.API_BASE_URL,
  credentials: "include",
  // Кастомная сериализация для Rails backend (поддержка массивов с [] скобками)
  querySerializer(queryParams) {
    const searchParams = [];

    for (const [name, value] of Object.entries(queryParams)) {
      if (value === null || value === undefined) {
        continue;
      }

      if (Array.isArray(value)) {
        // Rails формат: buyer_ids[]=9&buyer_ids[]=12
        for (const item of value) {
          if (item !== null && item !== undefined) {
            searchParams.push(
              `${encodeURIComponent(name)}[]=${encodeURIComponent(String(item))}`,
            );
          }
        }
      } else {
        // Обычные параметры
        searchParams.push(
          `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`,
        );
      }
    }

    return searchParams.join("&");
  },
});

// React Query клиенты
export const rqClient = createClient(fetchClient);
export const publicRqClient = createClient(publicFetchClient);

// Middleware для авторизованных запросов
fetchClient.use({
  async onRequest({ request }) {
    // Пропускаем retry запросы (уже обработаны в onResponse)
    if ((request as any)._isRetry) return;

    request.headers.set("Accept-Language", navigator.language || "ru");
    const authHeaders = useSession.getState().getAuthHeaders();

    if (authHeaders) {
      Object.entries(authHeaders).forEach(([key, value]) => {
        request.headers.set(key, value);
      });
    }

    // Кэшируем тело для возможного retry после 401: native fetch потребляет
    // request.body как ReadableStream, поэтому в onResponse оригинал недоступен.
    if (request.body && request.method !== "GET" && request.method !== "HEAD") {
      (request as any)._capturedBody = await request.clone().arrayBuffer();
    }
  },

  async onResponse({ response, request }) {
    // Обработка бана пользователя (403 с code: user_banned)
    if (response.status === 403) {
      try {
        const clonedResponse = response.clone();
        const data = await clonedResponse.json();
        if (data?.error?.code === "user_banned") {
          const message = data.error.message || "Ваш аккаунт заблокирован";
          const bannedUntil = data.error.banned_until;

          showBan({
            message,
            isPermanent: !bannedUntil,
            bannedUntil,
          });

          useSession.getState().logout(false); // без редиректа - диалог сам перенаправит
          return response;
        }
      } catch {
        // Если не JSON, продолжаем как обычно
      }
    }

    // Автоматический refresh токена при 401
    if (response.status === 401) {
      // Защита от зацикливания на refresh endpoint
      if (request.url.includes("/api/v1/tokens")) {
        useSession.getState().logout();
        return response;
      }

      // Пропускаем sign_out
      if (request.url.includes("/sign_out")) {
        return response;
      }

      const newTokens = await useSession.getState().refreshTokens();

      if (newTokens) {
        // Создаём новый Headers с обновлёнными auth headers
        const newHeaders = new Headers();
        request.headers.forEach((value, key) => newHeaders.set(key, value));
        const authHeaders = useSession.getState().getAuthHeaders();
        if (authHeaders) {
          Object.entries(authHeaders).forEach(([key, value]) => {
            newHeaders.set(key, value);
          });
        }

        // Retry с native fetch (middleware уже обработало этот запрос)
        // Флаг _isRetry добавляем в request для предотвращения повторной обработки
        const retryRequest = new Request(request.url, {
          method: request.method,
          body: (request as any)._capturedBody ?? null,
          headers: newHeaders,
          credentials: "include",
        });
        (retryRequest as any)._isRetry = true;

        return fetch(retryRequest);
      }
      // Если refresh не удался, logout уже вызван внутри refreshTokens
    }

    return response;
  },
});

publicFetchClient.use({
  async onRequest({ request }) {
    request.headers.set("Accept-Language", navigator.language || "ru");
  },
});
